import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { DonorAppliance } from "@/lib/sparePartDonor";
import { scopeToCompany } from "@/lib/activeCompanyLookup";
import { logApplianceFieldChanges } from "@/lib/serialAudit";
import {
  HARVEST_APPLIANCE_CATEGORIES,
  changedPatch,
  fieldsToShow,
  type CompletableField,
  derivedNameFor,
} from "@/lib/applianceDonorCompletion";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Props {
  open: boolean;
  appliance: DonorAppliance | null;
  companyId: string | null | undefined;
  /** The rules sheet doesn't recognise the current brand: show it for review. */
  reviewBrand?: boolean;
  onSaved: (updated: DonorAppliance) => void;
  onCancel: () => void;
}

export function CompleteApplianceDialog({ open, appliance, companyId, reviewBrand, onSaved, onCancel }: Props) {
  const { t } = useTranslation("harvest");
  const { fields: missing, review } = useMemo(() => fieldsToShow(appliance, { reviewBrand }), [appliance, reviewBrand]);
  const [values, setValues] = useState<Record<CompletableField, string>>({ category: "", brand: "", model: "", serial: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setValues({ category: "", brand: review.includes("brand") ? (appliance?.brand ?? "").trim() : "", model: "", serial: "" });
  }, [open, appliance?.id, review.join(",")]);

  const complete = missing.every((f) => values[f].trim().length > 0);

  const save = async () => {
    if (!appliance || !complete) return;
    const patch = changedPatch(appliance, missing, values);
    // Brand confirmed unchanged and nothing else to save: done, no write, no log.
    if (Object.keys(patch).length === 0) { onSaved(appliance); return; }
    setSaving(true);
    try {
      if (UUID_RE.test(appliance.id)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const name = derivedNameFor(appliance, patch);
        const base: any = supabase.from("appliances").update(name ? { ...patch, name } : patch).eq("id", appliance.id);
        const { data, error } = await scopeToCompany(base, companyId).select("id");
        if (error) throw error;
        if (!data || data.length === 0) {
          toast.error(t("completeAppliance.notAllowed"));
          return;
        }
        void logApplianceFieldChanges(
          appliance.id,
          (Object.keys(patch) as (keyof typeof patch)[]).map((col) => ({ field: col, oldValue: appliance[col], newValue: patch[col]! })),
        );
      }
      toast.success(t("completeAppliance.saved"));
      onSaved({ ...appliance, ...patch });
    } catch (e) {
      console.error("[complete-appliance] save failed", e);
      toast.error(t("completeAppliance.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open && missing.length > 0} onOpenChange={(o) => { if (!o && !saving) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("completeAppliance.title")}</DialogTitle>
          <DialogDescription>{t("completeAppliance.intro")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {missing.map((f) => (
            <div key={f} className="space-y-1.5" data-testid={`complete-field-${f}`}>
              <Label htmlFor={`complete-${f}`}>{t(`completeAppliance.field.${f}`)}</Label>
              {f === "category" ? (
                <Select value={values.category} onValueChange={(v) => setValues((p) => ({ ...p, category: v }))} disabled={saving}>
                  <SelectTrigger id="complete-category"><SelectValue placeholder={t("completeAppliance.choose")} /></SelectTrigger>
                  <SelectContent>
                    {HARVEST_APPLIANCE_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{t(`completeAppliance.category.${c.replace(/\s+/g, "")}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={`complete-${f}`}
                  maxLength={100}
                  autoComplete="off"
                  value={values[f]}
                  disabled={saving}
                  className={f === "serial" ? "font-mono" : undefined}
                  onChange={(e) => setValues((p) => ({ ...p, [f]: e.target.value }))}
                />
              )}
              {review.includes(f) && (
                <p className="text-xs text-muted-foreground">{t("completeAppliance.brandNotRecognised")}</p>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>{t("completeAppliance.cancel")}</Button>
          <Button onClick={() => void save()} disabled={saving || !complete}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {missing.length === 1 && review.includes("brand") && values.brand.trim() === (appliance?.brand ?? "").trim()
              ? t("completeAppliance.keepBrand")
              : t("completeAppliance.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
